import React from 'react'

// import PropTypes from 'prop-types'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'

import CompetitionService from 'src/services/CompetitionService'
import UploadService from 'src/services/UploadService'

import { route } from 'src/utils/routes'
import { IProduct } from 'src/services/types/product'

import ImageUploader from 'src/components/Image/ImageUploader'

const competitionService = new CompetitionService()
const uploadService = new UploadService()

interface IProps extends RouteComponentProps {
  user: any
  type?: string
  product?: IProduct
  updateProduct?: (updatedValues: IProduct) => void
  history: any
}

const ProductForm: React.FC<IProps> = ({
  user,
  type,
  product,
  updateProduct,
  history,
}) => {
  const { addToast } = useToasts()
  const _isMounted = React.useRef(false)

  const [isUploading, setIsUploading] = React.useState<boolean>(false)

  React.useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  const formConfig = {
    initialValues: {
      name: product ? product.name : '',
      status: product ? product.status : 'active',
      sku_id: product ? product.sku_id : '',
      price: product ? product.price : '',
      size: product ? product.size : '',
      weight: product ? product.weight : '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Name field is required.')
        .min(3, 'Name must be at least 3 characters.'),
      status: Yup.string().required('Status field is required.'),
      sku_id: Yup.string().required('SKU ID field is required.'),
      price: Yup.number().required('Price field is required.'),
      size: Yup.number().required('Size field is required.'),
      weight: Yup.number().required('Weight field is required.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      let finalValues: any = {}

      if (type === 'update') {
        finalValues._id = product._id
        finalValues.upload_id = product.upload_id
      }

      finalValues = {
        ...finalValues,
        name: values.name,
        status: values.status,
        sku_id: values.sku_id,
        price: values.price,
        size: values.size,
        weight: values.weight,
        manufacturer_id: user.manufacturer_id,
      }

      competitionService[type]({ ...finalValues })
        .then((productId) => {
          setSubmitting(false)
          // resetForm()

          if (type === 'update') {
            addToast('Competition product successfully updated.', {
              appearance: 'success',
            })
            updateProduct(finalValues)
          } else {
            addToast('Competition product successfully created.', {
              appearance: 'success',
            })
            history.push(route('competition_update', { id: productId }))
          }
        })
        .catch((error) => {
          setSubmitting(false)
          addToast(error.message, { appearance: 'error' })
        })
    },
  }

  const doUpload = (uploadData): Promise<any> => {
    return new Promise((resolve) => {
      uploadService
        .upload(uploadData, `competition_${product._id}`)
        .then((uploadId) => {
          const updatedProduct = {
            _id: product._id,
            name: product.name,
            status: product.status,
            sku_id: product.sku_id,
            price: product.price,
            manufacturer_id: user.manufacturer_id,
            upload_id: uploadId,
          }

          competitionService
            .update(updatedProduct)
            .then(() => {
              setIsUploading(false)
              addToast('Image upload successful.', {
                appearance: 'success',
              })
              updateProduct(updatedProduct)
              resolve()
            })
            .catch((error) => {
              setIsUploading(false)
              addToast(error.message, { appearance: 'error' })
              resolve()
            })
        })
        .catch((error) => {
          setIsUploading(false)
          addToast(error.message, { appearance: 'error' })
          resolve()
        })
    })
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formConfig.initialValues}
      validationSchema={formConfig.validationSchema}
      onSubmit={formConfig.onSubmit}
    >
      {({ errors, touched, values, handleSubmit, ...formik }) => (
        <Form onSubmit={handleSubmit}>
          {/* Competition product status */}
          <Form.Group as={Row} className="align-items-center">
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Status *</Form.Label>
            </Col>
            <Col md={6}>
              <div className="d-flex">
                <Field
                  as={Form.Check}
                  type="radio"
                  id="active"
                  name="status"
                  value="active"
                  label="Active"
                  checked={values.status === 'active'}
                  custom
                />

                <Field
                  as={Form.Check}
                  type="radio"
                  id="inactive"
                  name="status"
                  value="inactive"
                  label="Inactive"
                  checked={values.status === 'inactive'}
                  className="ml-3"
                  custom
                />
              </div>
            </Col>
          </Form.Group>
          <hr />

          {type === 'update' && product ? (
            <>
              {/* Image */}
              <Form.Group as={Row}>
                <Col md={3} className="mb-2 mb-md-0">
                  <Form.Label>Image</Form.Label>
                  <Form.Text className="text-muted">Product image</Form.Text>
                </Col>
                <Col md={6}>
                  <ImageUploader
                    uploadPath={product.upload ? product.upload.path : null}
                    doUpload={doUpload}
                  />
                </Col>
              </Form.Group>
              <hr />
            </>
          ) : null}

          {/* Product name */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Name *</Form.Label>
              <Form.Text className="text-muted">Name of product</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="name"
                type="text"
                isInvalid={touched.name && errors.name ? true : false}
              />
              <ErrorMessage
                name="name"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* SKU ID */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>SKU ID *</Form.Label>
              <Form.Text className="text-muted">Product SKU ID</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="sku_id"
                type="text"
                isInvalid={touched.sku_id && errors.sku_id ? true : false}
              />
              <ErrorMessage
                name="sku_id"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product price */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Price *</Form.Label>
              <Form.Text className="text-muted">Product price.</Form.Text>
            </Col>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Prepend>
                  <InputGroup.Text id="currency">₦</InputGroup.Text>
                </InputGroup.Prepend>
                <Field
                  as={Form.Control}
                  name="price"
                  type="number"
                  isInvalid={touched.price && errors.price ? true : false}
                />
              </InputGroup>
              <ErrorMessage
                name="price"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product size */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Size *</Form.Label>
              <Form.Text className="text-muted">Product size.</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="size"
                type="number"
                isInvalid={touched.size && errors.size ? true : false}
              />
              <ErrorMessage
                name="size"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product weight */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Weight *</Form.Label>
              <Form.Text className="text-muted">Product weight.</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="weight"
                type="number"
                isInvalid={touched.weight && errors.weight ? true : false}
              />
              <ErrorMessage
                name="weight"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          <Button
            type="submit"
            variant="success"
            className="mt-4"
            disabled={
              formik.isSubmitting ||
              !formik.dirty ||
              !formik.isValid ||
              isUploading
            }
          >
            {formik.isSubmitting ? (
              <figure className="spinner button white" />
            ) : type === 'create' ? (
              'Create Product'
            ) : (
              'Update Product'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

ProductForm.defaultProps = {
  type: 'create',
}

export default withRouter(ProductForm)
